import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { POSITIONS } from "../../../shared/constants/userConstants";
import { NewUser, newUserSchema } from "../../../shared/utils/userValidation";
import { profileClient } from "@/shared/api/clients";
import { useOrgId } from "../../../shared/auth/orgContext";

export interface AddUserFormProps {
  onUserAdded: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  stationId?: string;
}

const initialUser: NewUser = {
  firstName: "",
  lastName: "",
  position: "",
  badgeNumber: "",
  department: "",
  email: "",
  isAdmin: false,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
};

const AddUserForm: React.FC<AddUserFormProps> = ({
  onUserAdded,
  onError,
  onSuccess,
  stationId,
}) => {
  const theme = useTheme();
  const organizationId = useOrgId();
  const effectiveStationId = stationId ?? organizationId;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NewUser>({
    resolver: zodResolver(newUserSchema),
    mode: "onBlur",
    defaultValues: initialUser,
  });

  // zodResolver hands the parsed (trimmed) values to the submit handler.
  const onSubmit = handleSubmit(async (values) => {
    if (!effectiveStationId) {
      onError(
        "Unable to add user: station or organization ID is missing. Please refresh and try again.",
      );
      return;
    }

    try {
      await profileClient.post(`/v1/stations/${effectiveStationId}/users`, {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        isAdmin: values.isAdmin ?? false,
        metadata: {
          division_name: values.department,
          job_title: values.position,
          badge_number: values.badgeNumber,
        },
        sendOnboardingEmail: true,
      });

      onSuccess("User added successfully!");
      reset(initialUser);
      onUserAdded();
    } catch (error) {
      const message = isAxiosError(error) ? error.response?.data?.error : null;
      onError(message || "Failed to add user. Please try again.");
    }
  });

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[400]}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <PersonAddIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Add Individual User
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Create a new user account manually
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ flex: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="First Name"
            required
            error={!!errors.firstName}
            helperText={errors.firstName?.message ?? ""}
            sx={fieldSx}
            {...register("firstName")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Last Name"
            required
            error={!!errors.lastName}
            helperText={errors.lastName?.message ?? ""}
            sx={fieldSx}
            {...register("lastName")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="position"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth required error={!!errors.position}>
                <InputLabel id="rank-label">Rank</InputLabel>
                <Select
                  labelId="rank-label"
                  label="Rank"
                  sx={{ borderRadius: 2 }}
                  {...field}
                >
                  {POSITIONS.map((rank) => (
                    <MenuItem key={rank} value={rank}>
                      {rank}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.position?.message ?? ""}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Badge Number"
            required
            error={!!errors.badgeNumber}
            helperText={errors.badgeNumber?.message ?? ""}
            sx={fieldSx}
            {...register("badgeNumber")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Department"
            required
            error={!!errors.department}
            helperText={errors.department?.message ?? ""}
            sx={fieldSx}
            {...register("department")}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            error={!!errors.email}
            helperText={errors.email?.message ?? ""}
            sx={fieldSx}
            {...register("email")}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="isAdmin"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="primary"
                  />
                }
                label="Grant Administrator Privileges"
              />
            )}
          />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: "auto",
            pt: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting || !effectiveStationId}
            startIcon={<PersonAddIcon />}
            sx={{
              width: 200,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            {isSubmitting ? "Adding User..." : "Add User"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddUserForm;
